import { useState, useEffect, useCallback } from "react";
import API from "../api";
import { LayoutDashboard, Users, CreditCard, Sparkles, RefreshCw, LogIn, Server, Cpu, DollarSign, TrendingUp, Boxes, ShieldCheck } from "lucide-react";

// Regelis Management — a distinct, premium cockpit into the Regelis product.
// Deliberately its own look (deep slate + emerald/violet) so it reads as a
// separate console from the rest of MIDTC. Dependency-free SVG charts.
const C = {
  bg:"#0b1020", panel:"#111830", panel2:"#0e1428", border:"rgba(255,255,255,.08)", borderS:"rgba(255,255,255,.16)",
  text:"#eef2ff", muted:"#9aa7c7", faint:"#64708f", emerald:"#34d399", violet:"#a78bfa", blue:"#60a5fa", amber:"#fbbf24", pink:"#f472b6",
};
const fmtMoney = (n) => "$" + (Number(n)||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtInt = (n) => (Number(n)||0).toLocaleString();
const monthLabel = (m) => { const [y,mo]=m.split("-"); return new Date(y,mo-1).toLocaleString(undefined,{month:"short"}); };

const panel = { background:C.panel, border:`1px solid ${C.border}`, borderRadius:16, padding:22 };

function Kpi({ icon:Icon, label, value, sub, accent }) {
  return (
    <div style={{ ...panel, padding:20, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", right:-18, top:-18, width:80, height:80, borderRadius:"50%", background:accent, opacity:.10 }} />
      <div style={{ display:"flex", alignItems:"center", gap:9, color:C.muted, fontSize:12.5, fontWeight:600, marginBottom:10 }}>
        <Icon size={16} color={accent} /> {label}
      </div>
      <div style={{ fontSize:30, fontWeight:800, letterSpacing:"-.5px", color:C.text }}>{value}</div>
      {sub && <div style={{ color:C.faint, fontSize:12, marginTop:3 }}>{sub}</div>}
    </div>
  );
}

// Simple SVG bar chart (monthly series).
function BarChart({ data, accent, height=170, fmt=fmtInt }) {
  if (!data?.length) return <div style={{ color:C.faint, padding:"30px 0", textAlign:"center" }}>No data yet.</div>;
  const max = Math.max(1, ...data.map(d=>d.value));
  const bw = 100/data.length;
  return (
    <svg viewBox="0 0 100 42" preserveAspectRatio="none" style={{ width:"100%", height }}>
      {data.map((d,i)=>{ const h=(d.value/max)*32; return (
        <g key={i}>
          <rect x={i*bw+bw*0.2} y={36-h} width={bw*0.6} height={h||0.4} rx="0.6" fill={accent} opacity={0.85}>
            <title>{d.label}: {fmt(d.value)}</title>
          </rect>
          <text x={i*bw+bw*0.5} y={40.5} fontSize="2.4" fill={C.faint} textAnchor="middle">{d.short}</text>
        </g>);})}
    </svg>
  );
}
function Donut({ segments, size=150 }) {
  const total = segments.reduce((s,x)=>s+x.value,0)||1; let acc=0; const r=52, cx=60, cy=60;
  return (
    <svg viewBox="0 0 120 120" style={{ width:size, height:size }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.panel2} strokeWidth="14"/>
      {segments.map((s,i)=>{ const frac=s.value/total; const dash=frac*2*Math.PI*r; const gap=2*Math.PI*r-dash;
        const el=(<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth="14"
          strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-acc*2*Math.PI*r} transform={`rotate(-90 ${cx} ${cy})`}><title>{s.label}: {s.value}</title></circle>);
        acc+=frac; return el; })}
      <text x={cx} y={cy-2} fontSize="15" fontWeight="800" fill={C.text} textAnchor="middle">{total}</text>
      <text x={cx} y={cy+12} fontSize="7" fill={C.faint} textAnchor="middle">licenses</text>
    </svg>
  );
}

const PLAN_COLORS = { starter:C.blue, pro:C.violet, complete:C.emerald };

export default function RegelisManagement() {
  const [tab, setTab] = useState("dashboard");
  const [ov, setOv] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [o,c] = await Promise.all([API.get("/regelis/overview"), API.get("/regelis/customers")]);
      setOv(o.data); setCustomers(c.data || []);
    } catch (e) { setErr(e.response?.data?.error || "Regelis control-plane is unavailable."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const impersonate = async (lic, company) => {
    setBusy(lic);
    try {
      const r = await API.post(`/regelis/customers/${lic}/impersonate`);
      window.open(r.data.url, "_blank", "noopener");
    } catch (e) { alert(e.response?.data?.error || "Could not open that customer's instance."); }
    finally { setBusy(null); }
  };

  const t = ov?.totals || {};
  const signups = (ov?.signups_by_month||[]).map(s=>({ label:s.m, short:monthLabel(s.m), value:s.n }));
  const planSeg = (ov?.plan_mix||[]).map(p=>({ label:p.plan, value:p.n, color:PLAN_COLORS[p.plan]||C.faint }));
  const TABS = [
    { key:"dashboard", label:"Dashboard", icon:LayoutDashboard },
    { key:"customers", label:"Customers", icon:Users },
    { key:"billing",   label:"Billing",   icon:CreditCard },
    { key:"trials",    label:"Trials",    icon:Sparkles },
  ];

  return (
    <div style={{ background:`radial-gradient(1100px 480px at 78% -12%, rgba(52,211,153,.10), transparent), radial-gradient(800px 400px at 0% 0%, rgba(167,139,250,.10), transparent), ${C.bg}`, minHeight:"100%", margin:-24, padding:24, color:C.text }}>
      {/* Header band */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20 }}>
        <div style={{ width:44, height:44, borderRadius:12, background:`linear-gradient(135deg, ${C.emerald}, ${C.violet})`, display:"grid", placeItems:"center", boxShadow:"0 8px 26px rgba(52,211,153,.3)" }}>
          <ShieldCheck size={22} color="#04140a"/>
        </div>
        <div>
          <div style={{ fontSize:22, fontWeight:800, letterSpacing:"-.5px" }}>Regelis Management</div>
          <div style={{ color:C.muted, fontSize:13 }}>Your window into every Regelis customer instance — without leaving MIDTC.</div>
        </div>
        <button onClick={load} style={{ marginLeft:"auto", ...btn }}><RefreshCw size={15}/> Refresh</button>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:22, flexWrap:"wrap" }}>
        {TABS.map(({key,label,icon:Icon})=>(
          <button key={key} onClick={()=>setTab(key)} style={{ ...tabBtn, ...(tab===key?tabActive:{}) }}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </div>

      {err && <div style={{ ...panel, borderLeft:`3px solid ${C.amber}`, color:C.amber }}>{err} <span style={{color:C.faint}}>— is the Regelis control-plane (Postgres :5433) running?</span></div>}
      {loading ? <div style={{ ...panel, textAlign:"center", color:C.muted }}>Loading Regelis data…</div> : !err && (<>

      {/* DASHBOARD */}
      {tab==="dashboard" && (<>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:14, marginBottom:18 }}>
          <Kpi icon={DollarSign} label="Monthly recurring revenue" value={fmtMoney(t.mrr)} sub="All active customers" accent={C.emerald}/>
          <Kpi icon={Users} label="Customers" value={fmtInt(t.customers)} sub={`${fmtInt(t.active_instances)} live instances`} accent={C.violet}/>
          <Kpi icon={Boxes} label="Licensed bundles deployed" value={fmtInt(t.bundles_deployed)} sub="Across all customers' workstations" accent={C.blue}/>
          <Kpi icon={Cpu} label="FTR AI resolutions" value={fmtInt(t.ftr_calls_month)} sub="This month" accent={C.pink}/>
          <Kpi icon={Sparkles} label="Free trials active" value={fmtInt(t.trials_active)} sub="7-day trial (planned)" accent={C.amber}/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
          <div style={panel}>
            <div style={hdr}><TrendingUp size={16} color={C.emerald}/> New customers by month</div>
            <BarChart data={signups} accent={C.emerald}/>
          </div>
          <div style={panel}>
            <div style={hdr}><Server size={16} color={C.violet}/> Plan mix</div>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <Donut segments={planSeg.length?planSeg:[{label:"none",value:0,color:C.faint}]}/>
              <div style={{ flex:1 }}>
                {planSeg.map(s=>(<div key={s.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7, fontSize:13 }}>
                  <span style={{ width:10, height:10, borderRadius:3, background:s.color }}/> <span style={{ textTransform:"capitalize" }}>{s.label}</span>
                  <span style={{ marginLeft:"auto", color:C.muted }}>{s.value}</span></div>))}
              </div>
            </div>
          </div>
        </div>
      </>)}

      {/* CUSTOMERS */}
      {tab==="customers" && (
        <div style={panel}>
          <div style={hdr}><Users size={16} color={C.violet}/> Regelis customers <span style={{color:C.faint, fontWeight:400}}>· {customers.length}</span></div>
          <div style={{ overflowX:"auto" }}>
          <table style={tbl}>
            <thead><tr>{["Company","Plan","Status","Instance","Bundles","Monthly spend",""].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {customers.map(c=>(
                <tr key={c.id} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={td}><div style={{fontWeight:700}}>{c.company_name}</div><div style={{color:C.faint,fontSize:12}}>{c.billing_email}</div></td>
                  <td style={td}><span style={{ ...pill, background:(PLAN_COLORS[c.plan]||C.faint)+"22", color:PLAN_COLORS[c.plan]||C.muted, textTransform:"capitalize" }}>{c.plan}</span></td>
                  <td style={td}><span style={{ color: c.status==="active"?C.emerald:C.faint }}>● {c.status}</span></td>
                  <td style={td}>{c.instance_url ? <a href={c.instance_url} target="_blank" rel="noreferrer" style={{color:C.blue}}>{(c.instance_url||"").replace(/^https?:\/\//,"")}</a> : <span style={{color:C.faint}}>not launched</span>}</td>
                  <td style={td}>{fmtInt(c.device_count)}</td>
                  <td style={td}><strong>{fmtMoney(c.monthly_spend)}</strong></td>
                  <td style={td}>{c.instance_url && <button disabled={busy===c.id} onClick={()=>impersonate(c.id,c.company_name)} style={{ ...btn, ...btnEmerald }}><LogIn size={14}/> Log in as admin</button>}</td>
                </tr>
              ))}
              {!customers.length && <tr><td colSpan={7} style={{ ...td, textAlign:"center", color:C.faint, padding:34 }}>No Regelis customers yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* BILLING */}
      {tab==="billing" && (
        <div style={panel}>
          <div style={hdr}><CreditCard size={16} color={C.emerald}/> Bottom-up billing — total monthly spend per customer</div>
          <div style={{ color:C.faint, fontSize:12.5, marginBottom:14 }}>
            Plan base + ${ov?.pricing?.perBundle}/licensed bundle + ${ov?.pricing?.perFtrCall}/FTR resolution.
          </div>
          <div style={{ overflowX:"auto" }}>
          <table style={tbl}>
            <thead><tr>{["Company","Plan base","Bundles","FTR (mo)","Monthly total"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {customers.map(c=>{ const base=ov?.pricing?.plan?.[c.plan]||0; return (
                <tr key={c.id} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={td}><strong>{c.company_name}</strong></td>
                  <td style={td}>{fmtMoney(base)} <span style={{color:C.faint,textTransform:"capitalize",fontSize:12}}>({c.plan})</span></td>
                  <td style={td}>{fmtInt(c.device_count)} × {fmtMoney(ov?.pricing?.perBundle)} = {fmtMoney(c.device_count*(ov?.pricing?.perBundle||0))}</td>
                  <td style={td}>{fmtInt(c.ftr_calls_month)} × {fmtMoney(ov?.pricing?.perFtrCall)} = {fmtMoney(c.ftr_calls_month*(ov?.pricing?.perFtrCall||0))}</td>
                  <td style={td}><strong style={{color:C.emerald}}>{fmtMoney(c.monthly_spend)}</strong></td>
                </tr>);})}
            </tbody>
            <tfoot><tr style={{ borderTop:`2px solid ${C.borderS}` }}>
              <td style={{ ...td, fontWeight:800 }} colSpan={4}>Total MRR</td>
              <td style={{ ...td, fontWeight:800, color:C.emerald }}>{fmtMoney(t.mrr)}</td>
            </tr></tfoot>
          </table>
          </div>
        </div>
      )}

      {/* TRIALS */}
      {tab==="trials" && (
        <div style={panel}>
          <div style={hdr}><Sparkles size={16} color={C.amber}/> Free-trial program</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:14, marginBottom:16 }}>
            <Kpi icon={Sparkles} label="Trials active" value="0" sub="This month" accent={C.amber}/>
            <Kpi icon={Users} label="Trials → paid" value="0" sub="Converted" accent={C.emerald}/>
          </div>
          <div style={{ background:C.panel2, border:`1px solid ${C.border}`, borderRadius:12, padding:16, color:C.muted, fontSize:13.5 }}>
            <strong style={{color:C.text}}>Program rules (planned):</strong> the 7-day free-trial instance launches after a customer <strong>signs an NDA</strong> and their <strong>estimated monthly spend meets the $500 minimum</strong>. This tab will track trials started, days remaining, and conversion once the trial flow ships.
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

const btn = { display:"inline-flex", alignItems:"center", gap:7, padding:"8px 14px", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", border:`1px solid ${C.border}`, background:"rgba(255,255,255,.05)", color:C.text };
const btnEmerald = { border:`1px solid ${C.emerald}55`, background:`${C.emerald}18`, color:C.emerald };
const tabBtn = { display:"inline-flex", alignItems:"center", gap:8, padding:"9px 16px", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer", border:`1px solid ${C.border}`, background:"transparent", color:C.muted };
const tabActive = { background:`linear-gradient(120deg, ${C.emerald}22, ${C.violet}22)`, color:C.text, border:`1px solid ${C.borderS}` };
const hdr = { display:"flex", alignItems:"center", gap:9, fontWeight:700, fontSize:15, marginBottom:16 };
const tbl = { width:"100%", borderCollapse:"collapse", fontSize:13.5 };
const th = { textAlign:"left", padding:"9px 12px", color:C.faint, fontSize:11.5, textTransform:"uppercase", letterSpacing:".04em" };
const td = { padding:"11px 12px", verticalAlign:"top" };
const pill = { fontSize:11.5, fontWeight:700, padding:"2px 10px", borderRadius:999 };
